import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, comics } from '@/mocks/comics';

function formatViews(views: number): string {
  if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return views.toString();
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === currentSlug);

  const filteredComics = useMemo(() => {
    let list = currentSlug === 'all' ? [...comics] : comics.filter((c) => c.category === currentSlug);
    if (sortBy === 'popular') {
      list.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      list.sort((a, b) => b.rating - a.rating);
    } else {
      list.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
    return list;
  }, [currentSlug, sortBy]);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: currentCategory?.name || '全部漫画' },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '全部'}漫画 - 基德漫画网`,
    description: `基德漫画网${currentCategory?.name || '全部'}漫画分类，汇聚无删减韩国漫画，下拉式免费韩漫大全在线阅读。`,
    url: `https://jidemanhua.com/category/${currentSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredComics.map((comic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'CreativeWork',
          name: comic.title,
          url: `https://jidemanhua.com/article/${comic.id}`,
        },
      })),
    },
  };

  const sortOptions = [
    { key: 'latest' as const, label: '最新更新', icon: 'ri-time-line' },
    { key: 'popular' as const, label: '最多阅读', icon: 'ri-fire-line' },
    { key: 'rating' as const, label: '最高评分', icon: 'ri-star-line' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="pt-20 pb-8 md:pb-10 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-4">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              {currentCategory?.name || '全部'}漫画
            </h1>
            <p className="text-sm text-foreground-600 mt-2">
              基德漫画网汇聚无删减韩国漫画，下拉式免费韩漫大全在线阅读流畅，优质韩漫作品持续上新。
            </p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-2">
            <span className="text-sm text-foreground-500 mr-1">排序：</span>
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  sortBy === opt.key
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${opt.icon} text-xs`}></i>
                {opt.label}
              </button>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
            {filteredComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/article/${comic.id}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-3">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - ${comic.category}漫画 - 基德漫画网`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500/90 text-background-50 text-xs font-semibold">
                      <i className="ri-star-fill text-[10px]"></i>
                      {comic.rating}
                    </span>
                  </div>
                  {comic.status === '已完结' && (
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-0.5 rounded-full bg-foreground-800/80 text-background-50 text-xs">
                        {comic.status}
                      </span>
                    </div>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                  {comic.title}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-foreground-400">{comic.author}</span>
                  <span className="text-xs text-foreground-300">·</span>
                  <span className="text-xs text-foreground-400">{formatViews(comic.views)}</span>
                </div>
                {comic.chapters_list[0] && (
                  <p className="text-xs text-accent-600 mt-1 line-clamp-1">
                    {comic.chapters_list[0].title}
                  </p>
                )}
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-4xl text-foreground-300"></i>
              <p className="mt-3 text-foreground-500">该分类暂无漫画作品</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}